import { useContext, useMemo } from "react";
import { Accordion, AccordionItem, Badge, ScrollableArea } from "@knowtex-ai/components";
import { MacrosContext } from "../../../utils/MacrosContext";
import { useSharedMacrosQuery, type SharedMacro } from "../../../hooks/useSharedMacros";

interface MacrosContextValue {
  userMacros?: Array<{ dot_phrase?: string }>;
}

const normalizeDotPhrase = (dotPhrase?: string) =>
  typeof dotPhrase === "string" ? dotPhrase.trim().toLowerCase() : "";

/** Read-only list of the dot phrases shared with the provider's specialty. */
export default function SharedDotPhrasesPanel() {
  const sharedMacros = useSharedMacrosQuery();
  const { userMacros } = (useContext(MacrosContext) || {}) as MacrosContextValue;

  const personalDotPhrases = useMemo(
    () =>
      new Set(
        (Array.isArray(userMacros) ? userMacros : [])
          .map((macro) => normalizeDotPhrase(macro?.dot_phrase))
          .filter(Boolean)
      ),
    [userMacros]
  );

  if (sharedMacros.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="shared-dot-phrases-heading"
      className="bg-white rounded-lg p-6 mt-4"
    >
      <h2 id="shared-dot-phrases-heading" className="text-xl text-purple-700 font-bold mb-2">
        Specialty Dot Phrases
      </h2>
      <p className="text-gray-600 italic mb-4">
        Shared with every provider in your specialty. Type one in the Smart Chart custom prompt or
        Smart Assist to use it. These can&apos;t be edited.
      </p>
      <Accordion>
        {sharedMacros.map((macro: SharedMacro) => {
          const isReplaced = personalDotPhrases.has(normalizeDotPhrase(macro.dot_phrase));
          return (
            <AccordionItem
              key={macro.dot_phrase}
              id={`shared-dot-phrase-${macro.dot_phrase.replace(/^\./, "")}`}
              trigger={
                <span className="flex items-center gap-3">
                  <span className="font-semibold text-purple-600">{macro.dot_phrase}</span>
                  {isReplaced && (
                    <Badge
                      variant="warning"
                      size="small"
                      tip="You have your own dot phrase with this name, so yours is used instead."
                    >
                      Replaced by yours
                    </Badge>
                  )}
                </span>
              }
            >
              {macro.keywords.length > 0 && (
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium text-gray-700">Keywords:</span>{" "}
                  {macro.keywords.join(", ")}
                </p>
              )}
              <ScrollableArea maxHeight={384}>
                <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800">
                  {macro.content}
                </pre>
              </ScrollableArea>
            </AccordionItem>
          );
        })}
      </Accordion>
    </section>
  );
}
